import React from 'react';
import { Box, Typography, Grid } from '@mui/material';
import { Controller } from 'react-hook-form';

import Input from '../../../common/form/Input';
import Select from '../../../common/form/Select';
import RadioButtons from '../../../common/buttons/RadioButtons';
import Checkbox from '../../../common/form/Checkbox';
import DatePicker from '../../../common/form/DatePicker';
import { FormValues } from './types';
import { stateOptions, countryOptions } from './constants';

interface VehicleTravelStepProps {
	formValues: FormValues;
	setValue: any;
	control: any;
	errors: any;
	isRequired?: boolean;
}

export default function VehicleTravelStep({
	formValues,
	setValue,
	control,
	errors,
	isRequired = true,
}: VehicleTravelStepProps) {
	return (
		<Box sx={{ py: 2 }}>
			{/* Preferred Airport */}
			<Typography variant="h6" sx={{ mb: 2, color: 'text.label' }}>
				Preferred Airport
			</Typography>
			<Grid container spacing={3} sx={{ mb: 4 }}>
				<Grid item xs={12}>
					<Controller
						name="homeAirport"
						control={control}
						rules={
							isRequired
								? { required: 'Home airport is required' }
								: {}
						}
						render={({ field, fieldState }) => (
							<Input
								{...field}
								label="Home airport"
								required={isRequired}
								fullWidth
								error={!!fieldState.error}
								helperText={fieldState.error?.message}
							/>
						)}
					/>
				</Grid>
			</Grid>

			{/* Travel Basics */}
			<Typography variant="h6" sx={{ mb: 3, color: 'text.label' }}>
				Travel Basics
			</Typography>
			<Grid container spacing={3}>
				<Grid item xs={12} sm={6}>
					<Controller
						name="tsaPrecheck"
						control={control}
						render={({ field }) => (
							<Input
								{...field}
								label="TSA PreCheck #"
								fullWidth
							/>
						)}
					/>
				</Grid>
				<Grid item xs={12} sm={6}>
					<Controller
						name="globalEntry"
						control={control}
						render={({ field }) => (
							<Input
								{...field}
								label="Global Entry #"
								fullWidth
							/>
						)}
					/>
				</Grid>
				<Grid item xs={12} sm={6}>
					<Controller
						name="passportNumber"
						control={control}
						render={({ field, fieldState }) => (
							<Input
								{...field}
								label="Passport #"
								fullWidth
								error={!!fieldState.error}
								helperText={fieldState.error?.message}
							/>
						)}
					/>
				</Grid>
				<Grid item xs={12} sm={6}>
					<Controller
						name="passportIssuingCountry"
						control={control}
						render={({ field }) => (
							<Select
								{...field}
								label="Passport issuing country"
								options={countryOptions}
								fullWidth
							/>
						)}
					/>
				</Grid>
				<Grid item xs={12} sm={6}>
					<Controller
						name="passportExpirationDate"
						control={control}
						render={({ field }) => (
							<DatePicker
								{...field}
								label="Passport expiration date"
								placeholder="MM/DD/YYYY"
								fullWidth
							/>
						)}
					/>
				</Grid>
				<Grid item xs={12}>
					<Controller
						name="noPassport"
						control={control}
						render={({ field }) => (
							<Checkbox
								items={[
									{
										id: 'noPassport',
										name: 'I do not have a passport',
										checked: field.value,
										handleOnChange: field.onChange,
									},
								]}
							/>
						)}
					/>
				</Grid>
				<Grid item xs={12}>
					<Controller
						name="willingToTravelDomestically"
						control={control}
						rules={
							isRequired
								? {
										required:
											'Please specify if you are willing to travel domestically',
								  }
								: {}
						}
						render={({ field, fieldState }) => (
							<RadioButtons
								label="Are you willing to travel domestically?"
								name="willingToTravelDomestically"
								options={[
									{ value: 'yes', label: 'Yes' },
									{ value: 'no', label: 'No' },
								]}
								value={field.value}
								onChange={field.onChange}
								required={isRequired}
								error={!!fieldState.error}
								helperText={fieldState.error?.message}
							/>
						)}
					/>
				</Grid>
				<Grid item xs={12}>
					<Controller
						name="willingToTravelInternationally"
						control={control}
						rules={
							isRequired
								? {
										required:
											'Please specify if you are willing to travel internationally',
								  }
								: {}
						}
						render={({ field, fieldState }) => (
							<RadioButtons
								label="Are you willing to travel internationally?"
								name="willingToTravelInternationally"
								options={[
									{ value: 'yes', label: 'Yes' },
									{ value: 'no', label: 'No' },
								]}
								value={field.value}
								onChange={field.onChange}
								required={isRequired}
								error={!!fieldState.error}
								helperText={fieldState.error?.message}
							/>
						)}
					/>
				</Grid>
			</Grid>

			{/* Vehicle Information */}

			<Typography variant="h6" sx={{ mb: 3, color: 'text.label' }}>
				Vehicle Information
			</Typography>
			<Grid container spacing={3}>
				<Grid item xs={12} sm={6}>
					<Controller
						name="driversLicenseNumber"
						control={control}
						rules={
							isRequired
								? { required: 'Driver license # is required' }
								: {}
						}
						render={({ field, fieldState }) => (
							<Input
								{...field}
								label="Driver license #"
								required={isRequired}
								fullWidth
								error={!!fieldState.error}
								helperText={fieldState.error?.message}
							/>
						)}
					/>
				</Grid>
				<Grid item xs={12} sm={6}>
					<Controller
						name="driversLicenseState"
						control={control}
						rules={
							isRequired
								? {
										required:
											'Driver license state is required',
								  }
								: {}
						}
						render={({ field, fieldState }) => (
							<Select
								{...field}
								label="Driver license state"
								options={stateOptions}
								fullWidth
								required={isRequired}
								error={!!fieldState.error}
								helperText={fieldState.error?.message}
							/>
						)}
					/>
				</Grid>
				<Grid item xs={12}>
					<Controller
						name="noDriversLicense"
						control={control}
						render={({ field }) => (
							<Checkbox
								items={[
									{
										id: 'noDriversLicense',
										name: 'I do not have a driver license',
										checked: field.value,
										handleOnChange: field.onChange,
									},
								]}
							/>
						)}
					/>
				</Grid>
				<Grid item xs={12}>
					<Controller
						name="hasVehicle"
						control={control}
						render={({ field }) => (
							<RadioButtons
								label="Do you have a vehicle?"
								name="hasVehicle"
								options={[
									{ value: 'yes', label: 'Yes' },
									{ value: 'no', label: 'No' },
								]}
								value={
									field.value === true
										? 'yes'
										: field.value === false
										? 'no'
										: ''
								}
								onChange={(event) =>
									field.onChange(event.target.value === 'yes')
								}
							/>
						)}
					/>
				</Grid>
				<Grid item xs={12}>
					<Controller
						name="willingToUseVehicleForProduction"
						control={control}
						render={({ field }) => (
							<RadioButtons
								label="Are you willing to use your vehicle for paid production purposes?"
								name="willingToUseVehicleForProduction"
								options={[
									{ value: 'yes', label: 'Yes' },
									{ value: 'no', label: 'No' },
								]}
								value={
									field.value === true
										? 'yes'
										: field.value === false
										? 'no'
										: ''
								}
								onChange={(event) =>
									field.onChange(event.target.value === 'yes')
								}
							/>
						)}
					/>
				</Grid>
				{formValues.willingToUseVehicleForProduction === true && (
					<>
						<Grid item xs={12}>
							<Controller
								name="vehicleType"
								control={control}
								render={({ field }) => (
									<RadioButtons
										label="If yes, what is your vehicle type?"
										name="vehicleType"
										options={[
											{
												value: 'sedan',
												label: 'Sedan',
											},
											{ value: 'suv', label: 'SUV' },
											{
												value: 'truck',
												label: 'Truck',
											},
											{ value: 'van', label: 'Van' },
										]}
										value={field.value}
										onChange={field.onChange}
									/>
								)}
							/>
						</Grid>
						<Grid item xs={12}>
							<Controller
								name="vehicleLocation"
								control={control}
								render={({ field }) => (
									<Input
										{...field}
										label="Where is your vehicle based?"
										fullWidth
									/>
								)}
							/>
						</Grid>
					</>
				)}
			</Grid>
		</Box>
	);
}
